import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonCard, IonCardContent, IonContent, IonImg, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const letterImageMapping = {
    A: "apple.png",
    B: "ball.png",
    C: "cat.png",
    D: "dog.png",
    E: "elephant.png",
    F: "fish.png",
    G: "grapes.png",
    H: "hat.png",
    I: "icecream.png",
    J: "jug.png",
    K: "kite.png",
    L: "lion.png",
    M: "monkey.png",
    N: "nest.png",
    O: "orange.png",
    P: "parrot.png",
    Q: "queen.png",
    R: "rabbit.png",
    S: "sun.png",
    T: "tiger.png",
    U: "umbrella.png",
    V: "van.png",
    W: "watch.png",
    X: "xylophone.png",
    Y: "yak.png",
    Z: "zebra.png"
};

const getRandomItem = (obj: { [x: string]: any; A?: string; B?: string; C?: string; D?: string; E?: string; F?: string; G?: string; H?: string; I?: string; J?: string; K?: string; L?: string; M?: string; N?: string; O?: string; P?: string; Q?: string; R?: string; S?: string; T?: string; U?: string; V?: string; W?: string; X?: string; Y?: string; Z?: string; }) => {
    const keys = Object.keys(obj);
    const randomKey = keys[Math.floor(Math.random() * keys.length)];
    return { letter: randomKey, image: obj[randomKey] };
};

const generateOptions = (correctLetter: string) => {
    const letters = Object.keys(letterImageMapping);
    const options = new Set([correctLetter]);
    while (options.size < 4) {
        const randomLetter = letters[Math.floor(Math.random() * letters.length)];
        options.add(randomLetter);
    }
    return Array.from(options).sort(() => Math.random() - 0.5);
};

const LetterObjectMatch: React.FC = () => {
    const [currentQuestion, setCurrentQuestion] = useState(getRandomItem(letterImageMapping));
    const [options, setOptions] = useState(generateOptions(currentQuestion.letter));
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        setOptions(generateOptions(currentQuestion.letter));
        document.title = "Object from Letter – Nursery English Worksheet | Mama’s Guidance"; // Set page title
    }, [currentQuestion]);

    const handleAnswer = (selectedLetter: string) => {
        if (selectedLetter === currentQuestion.letter) {
            setShowImage(true);
            setTimeout(() => {
                setCurrentQuestion(getRandomItem(letterImageMapping));
                setShowImage(false);
            }, 5000);
        } else {
            setFeedback("❌ Try Again!");
        }
    };

    const nextQuestion = () => {
        setFeedback("");
        setCurrentQuestion(getRandomItem(letterImageMapping));
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Explore the "Object from Letter" activity for nursery kids on Mamas Guidance—an interactive way to help children identify objects that begin with each alphabet letter, enhancing early vocabulary and letter recognition skills.' />
            </Helmet>

            <div className="contentPart additionWord matchImage letterOption match-container objectLetter" style={{ textAlign: "center", padding: "20px" }}>
                {
                    showImage === false ?
                        <>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Object from Letter</h1>

                                    <p>
                                        Strengthen your child’s phonics and vocabulary skills with our fun <strong>Object from Letter</strong> worksheet! This activity encourages children to <strong>identify objects that start with each letter of the alphabet</strong>. It&apos;s an excellent early reading exercise for preschoolers and nursery-level learners.
                                    </p>

                                    <h2>🔤 What’s Inside the Worksheet?</h2>
                                    <p>
                                        Each question shows a <strong>letter (A–Z)</strong>, and your child is asked to choose or circle the object that begins with that letter sound — like <b><em>“A” for Apple</em>, <em>“B” for Ball</em>, <em>“C” for Cat</em></b>, and so on.
                                    </p>

                                    <h2>🎯 What Will Children Learn?</h2>
                                    <ul>
                                        <li>Recognize <strong>beginning sounds</strong> (phonics)</li>
                                        <li>Identify objects that match alphabet letters</li>
                                        <li>Build <strong>vocabulary</strong> and sound association</li>
                                        <li>Improve reading <strong>readiness and listening skills</strong></li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use This Worksheet</h2>
                                    <ul>
                                        <li>Show your child the letter on the page.</li>
                                        <li>Say the letter sound aloud and ask: <strong>“Which object starts with this sound?”</strong></li>
                                        <li>Let them circle, tick, or point to the correct object.</li>
                                        <li>Repeat for other letters and sounds.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Object With Letter</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/english-language/circle-correct-letter"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Circle Correct Letter</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/english-language/before-after-between-letters"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Letters</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why Beginning Sounds Matter</h2>
                                    <p>
                                        Beginning sounds are the <strong>building blocks</strong> of phonics. Recognizing which objects start with which letter helps children <strong>decode words</strong>, <strong>improves speech</strong>, and lays the foundation for <strong>spelling</strong> and <strong>reading</strong>.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Object from Letter</h1>

                                <p>
                                    Strengthen your child’s phonics and vocabulary skills with our fun <strong>Object from Letter</strong> worksheet! This activity encourages children to <strong>identify objects that start with each letter of the alphabet</strong>. It&apos;s an excellent early reading exercise for preschoolers and nursery-level learners.
                                </p>

                                <h2>🔤 What’s Inside the Worksheet?</h2>
                                <p>
                                    Each question shows a <strong>letter (A–Z)</strong>, and your child is asked to choose or circle the object that begins with that letter sound — like <b><em>“A” for Apple</em>, <em>“B” for Ball</em>, <em>“C” for Cat</em></b>, and so on.
                                </p>

                                <h2>🎯 What Will Children Learn?</h2>
                                <ul>
                                    <li>Recognize <strong>beginning sounds</strong> (phonics)</li>
                                    <li>Identify objects that match alphabet letters</li>
                                    <li>Build <strong>vocabulary</strong> and sound association</li>
                                    <li>Improve reading <strong>readiness and listening skills</strong></li>
                                </ul>

                                <h2>👩‍🏫 How to Use This Worksheet</h2>
                                <ul>
                                    <li>Show your child the letter on the page.</li>
                                    <li>Say the letter sound aloud and ask: <strong>“Which object starts with this sound?”</strong></li>
                                    <li>Let them circle, tick, or point to the correct object.</li>
                                    <li>Repeat for other letters and sounds.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Object With Letter</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/english-language/circle-correct-letter"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Circle Correct Letter</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/english-language/before-after-between-letters"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Letters</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why Beginning Sounds Matter</h2>
                                <p>
                                    Beginning sounds are the <strong>building blocks</strong> of phonics. Recognizing which objects start with which letter helps children decode words, improves speech, and lays the foundation for spelling and reading.
                                </p>
                            </article>
                            <h2>Which letter does this object start with?</h2>
                            <IonImg src={process.env.REACT_APP_API_URL + currentQuestion.image} alt={currentQuestion.letter} style={{ width: "150px", height: "150px" }} />
                            <div className="mt-4 grid-container object-letter">
                                {options.map((letter) => (
                                    <div className="optionButton background-blue" key={letter} onClick={() => handleAnswer(letter)}>{letter}</div>
                                ))}
                            </div>
                            <h3>{feedback}</h3>
                        </>
                        :
                        <>
                            <div className="contentPart showMiddle">
                                <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                            </div>
                        </>
                }
            </div>

        </>
    );
};

export default LetterObjectMatch;
